"use client";
import { useState, useEffect, useMemo, useRef } from "react";
import { createPortal } from "react-dom";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import SubAgreementModal, {
  API,
  fmt,
  cls,
  STATUS_STYLE,
  projectLabel,
  woLabel,
} from "./SubAgreementModal";

// ── Column definitions ───────────────────────────────────────────────────────
// get  → the text shown in the cell AND the value used by the column filter
// sort → comparable value (number or string) used when sorting
const dateStr = (d) =>
  d ? new Date(d).toLocaleDateString("en-CA", { timeZone: "UTC" }) : "—";
const ts = (d) => (d ? new Date(d).getTime() : 0);
const money = (n) => `$${fmt(n)}`;

const signedState = (a) =>
  a.signedAt
    ? a.signature?.name
      ? "E-signed"
      : "Paper copy"
    : a.sendInfo?.sentAt
      ? "Sent – awaiting"
      : "Not sent";

const STATUS_TEXT = { issued: "Issued", completed: "Completed", paid: "Paid" };

const COLUMNS = [
  {
    key: "number",
    label: "Agreement",
    get: (a) => a.agreementNumber || "—",
    sort: (a) => a.agreementNumber || "",
  },
  {
    key: "project",
    label: "Project",
    get: (a) => projectLabel(a),
    sort: (a) => projectLabel(a),
  },
  {
    key: "wo",
    label: "Work Order",
    get: (a) => woLabel(a),
    sort: (a) => woLabel(a),
  },
  {
    key: "sub",
    label: "Subcontractor",
    get: (a) => a.subInfo?.businessName || a.subContractor?.displayName || "—",
    sort: (a) => a.subInfo?.businessName || a.subContractor?.displayName || "",
  },
  {
    key: "title",
    label: "Title",
    get: (a) => a.title || "—",
    sort: (a) => a.title || "",
  },
  {
    key: "type",
    label: "Type",
    get: (a) => (a.union === "non-union" ? "Non-Union" : a.union || "—"),
    sort: (a) => a.union || "",
  },
  {
    key: "scheduled",
    label: "Scheduled",
    get: (a) => dateStr(a.scheduledDate),
    sort: (a) => ts(a.scheduledDate),
  },
  {
    key: "status",
    label: "Status",
    get: (a) => STATUS_TEXT[a.status] || a.status || "—",
    sort: (a) => a.status || "",
  },
  {
    key: "signed",
    label: "Signed",
    get: signedState,
    sort: signedState,
  },
  {
    key: "subtotal",
    label: "Subtotal",
    right: true,
    get: (a) => money(a.subtotal),
    sort: (a) => a.subtotal || 0,
  },
  {
    key: "total",
    label: "Total",
    right: true,
    get: (a) => money(a.total),
    sort: (a) => a.total || 0,
  },
  {
    key: "created",
    label: "Created",
    get: (a) =>
      a.createdAt ? new Date(a.createdAt).toLocaleDateString("en-CA") : "—",
    sort: (a) => ts(a.createdAt),
  },
];

const cmp = (x, y) =>
  typeof x === "number" && typeof y === "number"
    ? x - y
    : String(x).localeCompare(String(y), undefined, { numeric: true });

const SIGNED_STYLE = {
  "E-signed": "bg-green-800/60 text-green-200",
  "Paper copy": "bg-teal-800/60 text-teal-200",
  "Sent – awaiting": "bg-blue-800/60 text-blue-200",
  "Not sent": "bg-gray-700 text-gray-400",
};

// ═════════════════════════════════════════════════════════════════════════════
// Excel-style filter dropdown (rendered in a portal so table scroll can't clip it)
// ═════════════════════════════════════════════════════════════════════════════
function FilterMenu({ col, options, selected, rect, onChange, onClose }) {
  const [q, setQ] = useState("");
  const ref = useRef(null);

  useEffect(() => {
    const down = (e) => {
      if (ref.current && !ref.current.contains(e.target)) onClose();
    };
    const key = (e) => e.key === "Escape" && onClose();
    const scroll = (e) => {
      if (ref.current && !ref.current.contains(e.target)) onClose();
    };
    document.addEventListener("mousedown", down);
    document.addEventListener("keydown", key);
    window.addEventListener("scroll", scroll, true);
    window.addEventListener("resize", onClose);
    return () => {
      document.removeEventListener("mousedown", down);
      document.removeEventListener("keydown", key);
      window.removeEventListener("scroll", scroll, true);
      window.removeEventListener("resize", onClose);
    };
  }, [onClose]);

  const visible = options.filter((o) =>
    o.value.toLowerCase().includes(q.trim().toLowerCase()),
  );
  const isChecked = (v) => !selected || selected.has(v);
  const allVisibleChecked =
    visible.length > 0 && visible.every((o) => isChecked(o.value));

  const toggleAll = () => {
    if (!q.trim()) {
      onChange(allVisibleChecked ? new Set() : undefined);
    } else {
      // like Excel: "Select all search results" replaces the selection
      onChange(new Set(visible.map((o) => o.value)));
    }
  };

  const toggle = (v) => {
    const cur = selected
      ? new Set(selected)
      : new Set(options.map((o) => o.value));
    if (cur.has(v)) cur.delete(v);
    else cur.add(v);
    onChange(options.every((o) => cur.has(o.value)) ? undefined : cur);
  };

  const left = Math.max(8, Math.min(rect.left, window.innerWidth - 280));
  const top = Math.min(rect.bottom + 4, window.innerHeight - 400);

  return createPortal(
    <div
      ref={ref}
      style={{ position: "fixed", top, left, width: 268, zIndex: 10000 }}
      className="bg-gray-800 border border-gray-600 rounded-lg shadow-2xl"
    >
      <div className="px-3 pt-3 pb-2 border-b border-gray-700 space-y-2">
        <p className="text-[11px] font-semibold text-orange-400 uppercase tracking-wide">
          {col.label}
        </p>
        <input
          autoFocus
          value={q}
          onChange={(e) => setQ(e.target.value)}
          placeholder="Search values…"
          className="w-full px-2 py-1.5 text-xs bg-gray-700 border border-gray-600 rounded text-gray-200 placeholder-gray-500 focus:border-orange-500 focus:outline-none"
        />
      </div>

      <div className="max-h-64 overflow-y-auto py-1">
        {visible.length === 0 ? (
          <p className="px-3 py-4 text-xs text-gray-500 text-center">
            No matching values
          </p>
        ) : (
          <>
            <label className="flex items-center gap-2 px-3 py-1.5 text-xs text-gray-300 hover:bg-gray-700/60 cursor-pointer border-b border-gray-700/60">
              <input
                type="checkbox"
                checked={allVisibleChecked}
                onChange={toggleAll}
                className="accent-orange-500"
              />
              <span className="font-medium">
                {q.trim() ? "Select all search results" : "(Select all)"}
              </span>
            </label>
            {visible.map((o) => (
              <label
                key={o.value}
                className="group flex items-center gap-2 px-3 py-1.5 text-xs text-gray-200 hover:bg-gray-700/60 cursor-pointer"
              >
                <input
                  type="checkbox"
                  checked={isChecked(o.value)}
                  onChange={() => toggle(o.value)}
                  className="accent-orange-500"
                />
                <span className="flex-1 truncate" title={o.value}>
                  {o.value}
                </span>
                <button
                  type="button"
                  onClick={(e) => {
                    e.preventDefault();
                    onChange(new Set([o.value]));
                  }}
                  className="hidden group-hover:inline text-[10px] text-orange-400 hover:text-orange-300"
                >
                  only
                </button>
                <span className="text-[10px] text-gray-500 font-mono">
                  {o.count}
                </span>
              </label>
            ))}
          </>
        )}
      </div>

      <div className="px-3 py-2 border-t border-gray-700 flex items-center justify-between">
        <button
          onClick={() => onChange(undefined)}
          disabled={!selected}
          className="text-xs text-gray-400 hover:text-white disabled:opacity-30"
        >
          Clear filter
        </button>
        <button
          onClick={onClose}
          className="px-3 py-1 text-xs font-medium text-white bg-orange-500 rounded hover:bg-orange-400"
        >
          Done
        </button>
      </div>
    </div>,
    document.body,
  );
}

// ═════════════════════════════════════════════════════════════════════════════
const PAGE = 200;

export default function SubAgreementsPage() {
  const navigate = useNavigate();
  const token = localStorage.getItem("token");
  const headers = useMemo(
    () => ({ Authorization: `Bearer ${token}` }),
    [token],
  );

  const [agreements, setAgreements] = useState([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");

  const [search, setSearch] = useState("");
  const [filters, setFilters] = useState({}); // { colKey: Set<string> }
  const [sortKey, setSortKey] = useState("created");
  const [sortDir, setSortDir] = useState("desc");
  const [openFilter, setOpenFilter] = useState(null); // { key, rect }
  const [viewFor, setViewFor] = useState(null);
  const [limit, setLimit] = useState(PAGE);

  const load = async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/subagreements3/all`, { headers });
      setAgreements(r.data.agreements || []);
      setErr("");
    } catch (e) {
      setErr(e.response?.data?.message || e.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Pre-compute every column value + a searchable blob per row
  const rows = useMemo(
    () =>
      agreements.map((a) => {
        const v = {};
        COLUMNS.forEach((c) => {
          v[c.key] = c.get(a);
        });
        const hay = [
          ...Object.values(v),
          a.scopeOfWork,
          a.signature?.name,
          ...(a.lineItems || []).map((l) => `${l.itemCode} ${l.description}`),
        ]
          .join(" ")
          .toLowerCase();
        return { a, v, hay };
      }),
    [agreements],
  );

  const terms = useMemo(
    () => search.toLowerCase().split(/\s+/).filter(Boolean),
    [search],
  );

  // Row passes search + every column filter except `skip`
  const passes = (r, skip) => {
    for (const t of terms) if (!r.hay.includes(t)) return false;
    for (const c of COLUMNS) {
      if (c.key === skip) continue;
      const f = filters[c.key];
      if (f && !f.has(r.v[c.key])) return false;
    }
    return true;
  };

  const filtered = useMemo(
    () => rows.filter((r) => passes(r, null)),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [rows, terms, filters],
  );

  const sorted = useMemo(() => {
    const col = COLUMNS.find((c) => c.key === sortKey) || COLUMNS[0];
    const dir = sortDir === "asc" ? 1 : -1;
    return [...filtered].sort(
      (x, y) => dir * cmp(col.sort(x.a), col.sort(y.a)),
    );
  }, [filtered, sortKey, sortDir]);

  // Options for the open dropdown: values among rows that pass every OTHER
  // filter, so the list narrows as the results narrow (Excel behaviour).
  const menuOptions = useMemo(() => {
    if (!openFilter) return [];
    const col = COLUMNS.find((c) => c.key === openFilter.key);
    const map = new Map();
    rows.forEach((r) => {
      if (!passes(r, openFilter.key)) return;
      const val = r.v[col.key];
      const e = map.get(val);
      if (e) e.count += 1;
      else map.set(val, { value: val, count: 1, sample: r.a });
    });
    return [...map.values()].sort((x, y) =>
      cmp(col.sort(x.sample), col.sort(y.sample)),
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [openFilter, rows, terms, filters]);

  const stats = useMemo(
    () =>
      filtered.reduce(
        (acc, r) => {
          acc.subtotal += r.a.subtotal || 0;
          acc.total += r.a.total || 0;
          if (r.a.signedAt) acc.signed += 1;
          return acc;
        },
        { subtotal: 0, total: 0, signed: 0 },
      ),
    [filtered],
  );

  const activeFilterKeys = COLUMNS.filter((c) => filters[c.key]).map(
    (c) => c.key,
  );
  const hasAnyFilter = activeFilterKeys.length > 0 || terms.length > 0;

  const toggleSort = (key) => {
    if (sortKey === key) setSortDir((d) => (d === "asc" ? "desc" : "asc"));
    else {
      setSortKey(key);
      setSortDir("asc");
    }
  };

  const setFilter = (key, set) =>
    setFilters((f) => {
      const next = { ...f };
      if (set === undefined) delete next[key];
      else next[key] = set;
      return next;
    });

  const clearAll = () => {
    setFilters({});
    setSearch("");
  };

  const openMenu = (key, e) => {
    const rect = e.currentTarget.getBoundingClientRect();
    setOpenFilter((prev) => (prev?.key === key ? null : { key, rect }));
  };

  const exportCsv = () => {
    const q = (s) => `"${String(s ?? "").replace(/"/g, '""')}"`;
    const lines = [
      COLUMNS.map((c) => q(c.label)).join(","),
      ...sorted.map((r) => COLUMNS.map((c) => q(r.v[c.key])).join(",")),
    ];
    const blob = new Blob(["\ufeff" + lines.join("\n")], {
      type: "text/csv;charset=utf-8",
    });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = "subcontractor-agreements.csv";
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    setTimeout(() => URL.revokeObjectURL(url), 10000);
  };

  const renderCell = (c, a, v) => {
    switch (c.key) {
      case "number":
        return <span className="font-mono text-orange-400">{v}</span>;
      case "project":
        return a.project?._id ? (
          <button
            onClick={(e) => {
              e.stopPropagation();
              navigate(`/Projects/${a.project._id}?tab=Work Orders`);
            }}
            className="text-gray-200 hover:text-orange-300 hover:underline text-left"
            title="Open project"
          >
            {v}
          </button>
        ) : (
          <span className="text-gray-400">{v}</span>
        );
      case "type":
        return (
          <span
            className={cls(
              "px-1.5 py-0.5 text-[9px] font-semibold rounded uppercase",
              a.union === "non-union"
                ? "bg-gray-700 text-gray-300"
                : "bg-teal-700 text-white",
            )}
          >
            {v}
          </span>
        );
      case "status":
        return (
          <span
            className={cls(
              "px-1.5 py-0.5 text-[9px] font-semibold rounded uppercase",
              STATUS_STYLE[a.status],
            )}
          >
            {v}
          </span>
        );
      case "signed":
        return (
          <span
            className={cls(
              "px-1.5 py-0.5 text-[10px] font-semibold rounded whitespace-nowrap",
              SIGNED_STYLE[v],
            )}
          >
            {a.signedAt ? "🔒 " : ""}
            {v}
          </span>
        );
      case "subtotal":
        return <span className="font-mono text-gray-300">{v}</span>;
      case "total":
        return (
          <span
            className={cls(
              "font-mono font-semibold",
              a.status === "issued" ? "text-yellow-300" : "text-green-300",
            )}
          >
            {v}
          </span>
        );
      default:
        return <span className="text-gray-300">{v}</span>;
    }
  };

  const openCol = openFilter && COLUMNS.find((c) => c.key === openFilter.key);

  return (
    <div className="max-w-full mx-auto px-4 py-6 space-y-4">
      {/* Header */}
      <div className="flex items-end justify-between gap-3 flex-wrap">
        <div>
          <h1 className="text-lg font-bold text-gray-800">
            Subcontractor Agreements
          </h1>
          <p className="text-xs text-gray-500">
            Every agreement across all projects. Click a row to view it.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={load}
            className="px-3 py-1.5 text-xs text-gray-300 bg-gray-700 rounded-lg hover:bg-gray-600"
          >
            ↻ Refresh
          </button>
          <button
            onClick={exportCsv}
            disabled={sorted.length === 0}
            className="px-3 py-1.5 text-xs font-semibold text-white rounded-lg bg-orange-500 hover:bg-orange-400 disabled:opacity-40"
          >
            Export CSV ({sorted.length})
          </button>
        </div>
      </div>

      <div className="bg-gray-800 rounded-xl border border-gray-700 overflow-hidden">
        {/* Search + stats */}
        <div className="px-4 py-3 border-b border-gray-700 flex flex-wrap items-center gap-3">
          <input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search anything — number, project, sub, scope, item…"
            className="px-3 py-1.5 text-sm bg-gray-700 border border-gray-600 rounded-lg text-gray-200 placeholder-gray-500 focus:border-orange-500 focus:outline-none w-full sm:w-96"
          />
          {hasAnyFilter && (
            <button
              onClick={clearAll}
              className="px-2.5 py-1.5 text-xs text-gray-300 bg-gray-700 rounded-lg hover:bg-gray-600"
            >
              ✕ Clear all
            </button>
          )}
          {loading && (
            <span className="text-xs text-gray-600 animate-pulse">
              Loading…
            </span>
          )}
          <div className="ml-auto flex items-center gap-4 text-xs text-gray-400 flex-wrap">
            <span>
              Showing{" "}
              <span className="text-gray-200 font-mono">{filtered.length}</span>{" "}
              of {rows.length}
            </span>
            <span>
              Signed:{" "}
              <span className="text-green-300 font-mono">{stats.signed}</span>
            </span>
            <span>
              Subtotal:{" "}
              <span className="text-gray-200 font-mono">
                ${fmt(stats.subtotal)}
              </span>
            </span>
            <span>
              Total:{" "}
              <span className="text-orange-300 font-mono font-semibold">
                ${fmt(stats.total)}
              </span>
            </span>
          </div>
        </div>

        {/* Active filter chips */}
        {activeFilterKeys.length > 0 && (
          <div className="px-4 py-2 border-b border-gray-700/60 flex flex-wrap gap-2">
            {activeFilterKeys.map((k) => {
              const c = COLUMNS.find((x) => x.key === k);
              const vals = [...filters[k]];
              const text =
                vals.length === 0
                  ? "(none)"
                  : vals.length <= 3
                    ? vals.join(", ")
                    : `${vals.slice(0, 3).join(", ")} +${vals.length - 3}`;
              return (
                <span
                  key={k}
                  className="inline-flex items-center gap-1.5 text-[11px] bg-orange-500/15 text-orange-200 border border-orange-500/30 rounded-full px-2.5 py-0.5"
                >
                  <span className="text-orange-400 font-semibold">
                    {c.label}:
                  </span>
                  <span className="max-w-[260px] truncate">{text}</span>
                  <button
                    onClick={() => setFilter(k, undefined)}
                    className="text-orange-300 hover:text-white"
                  >
                    ✕
                  </button>
                </span>
              );
            })}
          </div>
        )}

        {err ? (
          <p className="px-5 py-6 text-sm text-red-400">{err}</p>
        ) : (
          <div className="overflow-auto max-h-[72vh]">
            <table className="w-full text-xs">
              <thead className="bg-gray-900 sticky top-0 z-10">
                <tr>
                  {COLUMNS.map((c) => {
                    const active = !!filters[c.key];
                    return (
                      <th
                        key={c.key}
                        className={cls(
                          "px-3 py-2.5 text-gray-400 font-medium whitespace-nowrap",
                          c.right ? "text-right" : "text-left",
                        )}
                      >
                        <div
                          className={cls(
                            "flex items-center gap-1",
                            c.right && "justify-end",
                          )}
                        >
                          <button
                            onClick={() => toggleSort(c.key)}
                            className="hover:text-gray-200"
                          >
                            {c.label}
                            {sortKey === c.key
                              ? sortDir === "asc"
                                ? " ▲"
                                : " ▼"
                              : ""}
                          </button>
                          <button
                            onMouseDown={(e) => e.stopPropagation()}
                            onClick={(e) => openMenu(c.key, e)}
                            title="Filter"
                            className={cls(
                              "px-1 rounded text-[10px] leading-4",
                              active
                                ? "bg-orange-500 text-white"
                                : "text-gray-500 hover:text-gray-200 hover:bg-gray-700",
                            )}
                          >
                            ▼
                          </button>
                        </div>
                      </th>
                    );
                  })}
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-700/50">
                {!loading && sorted.length === 0 ? (
                  <tr>
                    <td
                      colSpan={COLUMNS.length}
                      className="px-3 py-12 text-center text-gray-500"
                    >
                      {rows.length === 0
                        ? "No subcontractor agreements yet."
                        : "No agreements match your filters."}
                    </td>
                  </tr>
                ) : (
                  sorted.slice(0, limit).map((r, i) => (
                    <tr
                      key={r.a._id}
                      onClick={() => setViewFor(r.a)}
                      className={cls(
                        "cursor-pointer hover:bg-gray-600/30 transition-colors",
                        i % 2 === 0 ? "bg-gray-800/40" : "bg-gray-700/20",
                        r.a.status === "issued" &&
                          "border-l-2 border-yellow-500/50",
                      )}
                    >
                      {COLUMNS.map((c) => (
                        <td
                          key={c.key}
                          className={cls(
                            "px-3 py-2",
                            c.right ? "text-right" : "text-left",
                            ["title", "wo", "project", "sub"].includes(c.key)
                              ? "max-w-[220px] truncate"
                              : "whitespace-nowrap",
                          )}
                          title={
                            ["title", "wo", "project", "sub"].includes(c.key)
                              ? r.v[c.key]
                              : undefined
                          }
                        >
                          {renderCell(c, r.a, r.v[c.key])}
                        </td>
                      ))}
                    </tr>
                  ))
                )}
              </tbody>
              {sorted.length > 0 && (
                <tfoot className="bg-gray-900 sticky bottom-0 border-t border-gray-600">
                  <tr>
                    {COLUMNS.map((c, idx) => (
                      <td
                        key={c.key}
                        className={cls(
                          "px-3 py-2.5",
                          c.right ? "text-right" : "text-left",
                        )}
                      >
                        {idx === 0 && (
                          <span className="text-xs text-gray-400">
                            {sorted.length} agreement
                            {sorted.length !== 1 ? "s" : ""}
                          </span>
                        )}
                        {c.key === "subtotal" && (
                          <span className="text-xs text-gray-300 font-mono">
                            ${fmt(stats.subtotal)}
                          </span>
                        )}
                        {c.key === "total" && (
                          <span className="text-sm font-bold text-orange-300 font-mono">
                            ${fmt(stats.total)}
                          </span>
                        )}
                      </td>
                    ))}
                  </tr>
                </tfoot>
              )}
            </table>
            {sorted.length > limit && (
              <div className="py-3 text-center border-t border-gray-700/50">
                <button
                  onClick={() => setLimit((l) => l + PAGE)}
                  className="px-4 py-1.5 text-xs text-gray-200 bg-gray-700 rounded-lg hover:bg-gray-600"
                >
                  Show {Math.min(PAGE, sorted.length - limit)} more (
                  {sorted.length - limit} remaining)
                </button>
              </div>
            )}
          </div>
        )}
      </div>

      {openFilter && openCol && (
        <FilterMenu
          key={openFilter.key}
          col={openCol}
          options={menuOptions}
          selected={filters[openFilter.key]}
          rect={openFilter.rect}
          onChange={(set) => setFilter(openFilter.key, set)}
          onClose={() => setOpenFilter(null)}
        />
      )}

      {viewFor && (
        <SubAgreementModal
          key={viewFor._id}
          agreement={viewFor}
          headers={headers}
          onClose={() => setViewFor(null)}
          onChanged={load}
        />
      )}
    </div>
  );
}
